import React, { useState, useEffect } from "react";
import { Button, Modal } from "flowbite-react";
import { UserInfoModel } from "../services/users/user.api.interface";
import usersApi from "../services/users/UserApi";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./CommonHeader";
import ToastNotification, { showToast } from "./Toaster";
import CustomDropdown from "./CustomDropdown";
import PillButton from "./button/Pills";

const UsersList = () => {
  const [userList, setUserList] = useState<UserInfoModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserInfoModel[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("active");
  const navigate = useNavigate();

  const fetchUserList = async (status: string) => {
    setLoading(true);
    try {
      const response = await usersApi.getUserList(1, status);
      if (response.success && response.value && Array.isArray(response.value.data.contents)) {
        const users = response.value.data.contents.map((user: any) => ({
          id: user.id,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email,
          phoneNumber: user.phoneNumber || "",
          status: user.status || "pending",
        }));
        setUserList(users);
      } else {
        console.error("Failed to fetch users:", response || "Unknown error");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserList(filterStatus);
  }, [filterStatus]);

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
  };

  const handleDeleteClick = (user: UserInfoModel) => {
    setSelectedUsers([user]);
    setOpenDeleteModal(true);
  };

  const handleUserSelection = (user: UserInfoModel) => {
    setSelectedUsers((prevSelected) => {
      if (prevSelected.some((u) => u.id === user.id)) {
        return prevSelected.filter((u) => u.id !== user.id);
      } else {
        return [...prevSelected, user];
      }
    });
  };

  const handleDeleteSelectedUsers = async () => {
    setOpenDeleteModal(true);
  };

  const confirmDeleteSelectedUsers = async () => {
    try {
      const userIds = selectedUsers.map((user) => user.id + "");
      const response = await usersApi.deleteUser(userIds);
      if (response.success) {
        setUserList((prevList) =>
          prevList.filter((u) => !userIds.includes(u.id.toString()))
        );
        setSelectedUsers([]);
        setOpenDeleteModal(false);
        showToast("Users deleted successfully!", "success");
      } else {
        console.error("Failed to delete users:", response.message);
      }
    } catch (error) {
      console.error("Error deleting users:", error);
    }
  };

  const getUserConfig = async (userId: number) => {
    try {
      const response = await usersApi.getUserConfig(userId);
      if (response.success) {
        navigate(`/superadmin/user/config/${userId}`);
      } else {
        console.error("Failed to fetch user config:", response.error.message);
      }
    } catch (error) {
      console.error("Error fetching user config:", error);
    }
  };

  return (
    <>
      <div className="flex w-full h-[calc(100vh-67px)]">
        <ToastNotification />
        <div className="flex-1">
          <CommonHeader title="User List" />

          {loading ? (
            <p className="text-gray-500 dark:text-gray-50 text-center mt-[200px]">Loading...</p>
          ) :  (
            <div className="w-full px-16 py-6">
              <div className="relative overflow-x-auto  h-[calc(100vh-267px)]">
                <div className="flex justify-end items-center h-[45px] mb-1">
                
                <div className="mr-2">
                  <CustomDropdown value={filterStatus} onChange={handleFilterChange} />
                </div>
               
                  {selectedUsers.length >= 1 ? (
                    <Button
                      className={`bg-red-500 text-white ${
                        selectedUsers.length >= 1 ? "" : "opacity-50 cursor-not-allowed"
                      }`}
                      disabled={selectedUsers.length < 1}
                      onClick={handleDeleteSelectedUsers}
                    >
                      Delete ({selectedUsers.length})
                    </Button>
                  ) : (
                    <div className="h-[38px]"></div>
                  )}
                </div>
                <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                  <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-900 dark:text-gray-400">
                      <tr>
                        <th scope="col" className="px-6 py-3">
                          <input
                            type="checkbox"
                            onChange={(e) =>
                              setSelectedUsers(
                                e.target.checked ? [...userList] : []
                              )
                            }
                            checked={
                              selectedUsers.length === userList.length &&
                              userList.length > 0
                            }
                          />
                        </th>
                        <th scope="col" className="px-6 py-3">Name</th>
                        <th scope="col" className="px-6 py-3">Email</th>
                        <th scope="col" className="px-6 py-3">Phone Number</th>
                        <th scope="col" className="px-6 py-3">Status</th>
                        <th scope="col" className="px-6 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userList.map((user) => (
                        <tr
                          key={user.id}
                          className="bg-white border-b dark:bg-gray-700 dark:border-gray-700"
                        >
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedUsers.some((u) => u.id === user.id)}
                              onChange={() => handleUserSelection(user)}
                            />
                          </td>
                          <th
                            scope="row"
                            className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white capitalize"
                          >
                            {user.firstName} {user.lastName}
                          </th>
                          <td className="px-6 py-4 dark:text-gray-50">{user.email}</td>
                          <td className="px-6 py-4 dark:text-gray-50">{user.phoneNumber}</td>
                          <td className="px-6 py-4 dark:text-gray-50 capitalize text-green-500 font-semibold">
                            {user.status}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              type="button"
                              className="action-btn"
                              onClick={() => handleDeleteClick(user)}
                            >
                              <svg
                                stroke="currentColor"
                                fill="currentColor"
                                strokeWidth="0"
                                viewBox="0 0 448 512"
                                height="1em"
                                width="1em"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="action-btn ml-2"
                              onClick={() => getUserConfig(user.id)}
                            >
                              <svg
                                stroke="currentColor"
                                fill="currentColor"
                                strokeWidth="0"
                                viewBox="0 0 512 512"
                                height="1em"
                                width="1em"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"></path>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                      {userList && userList.length === 0 ? (
                        <tr className="text-gray-500 text-center">
                          <td className="py-4 font-semibold" colSpan={6}>
                            No records found.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        show={openDeleteModal}
        size="md"
        className="fixed inset-0 flex items-center justify-center z-50"
        onClose={() => setOpenDeleteModal(false)}
        popup
      >
        <div className="h-[300px]">
          <Modal.Header className="dark:bg-gray-800 bg-gray-200 rounded-t-lg">
            <h3 className="text-lg font-bold text-gray-600 dark:text-gray-100">
              Delete User(s)
            </h3>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mt-5">
              <h3 className="mb-5 text-lg font-normal text-gray-600 dark:text-gray-100 mt-10">
                Are you sure you want to delete the selected user(s)?
              </h3>
              <div className="flex justify-center gap-4 mt-10">
                <PillButton
                  className="w-[80px] bg-red-500 text-white"
                  onClick={confirmDeleteSelectedUsers}
                >
                  Yes
                </PillButton>
                <PillButton
                  variant="primary"
                  className="dark:bg-gray-900 hover:dark:bg-gray-800 text-gray-900"
                  onClick={() => setOpenDeleteModal(false)}
                >
                  Cancel
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </div>
      </Modal>
    </>
  );
};

export default UsersList;